import { RRule, RRuleSet, Frequency, Options as RRuleOptions, rrulestr } from 'rrule';
import dayjs from 'dayjs';

/**
 * Options pour la fréquence de récurrence
 */
export const frequencyOptions = [
  { value: Frequency.DAILY, label: 'jours' },
  { value: Frequency.WEEKLY, label: 'semaines' },
  { value: Frequency.MONTHLY, label: 'mois' },
];

/**
 * Convertit une chaîne RRule en RRuleSet
 * @param rruleString - Chaîne RRule à convertir
 * @param exdates - Dates à exclure de la récurrence
 * @returns RRuleSet configuré
 */
const stringToRRuleSet = (rruleString: string, exdates: Date[] = []): RRuleSet => {
  try {
    const rruleSet: RRuleSet = rruleString
      ? (rrulestr(rruleString, { forceset: true }) as RRuleSet)
      : new RRuleSet();

    return rruleSet;
  } catch (error) {
    console.error('Error converting RRule string to RRuleSet:', error);
    return new RRuleSet();
  }
};

/**
 * Convertit un RRuleSet en chaîne de caractères
 * @param rruleSet - RRuleSet à convertir
 * @returns Chaîne représentant le RRuleSet
 */
const rruleSetToString = (rruleSet: RRuleSet): string => {
  // Récupère la première règle du set (règle principale)
  const rules = rruleSet.rrules();
  if (rules.length > 0) {
    return rules[0].toString();
  }
  return '';
};

export const getRRuleDetails = (
  rruleString: string
): {
  frequency: number | null;
  interval: number | null;
  repeatFrom: Date | null;
  repeatUntil: Date | null;
} => {
  try {
    const rruleSet = stringToRRuleSet(rruleString);
    const rules = rruleSet.rrules();
    if (rules.length > 0) {
      const rule = rules[0];
      return {
        frequency: rule.options.freq,
        interval: rule.options.interval || 1,
        repeatFrom: rule.options.dtstart,
        repeatUntil: rule.options.until,
      };
    }
  } catch (error) {
    console.error('Error getting RRule details:', error);
  }
  return {
    frequency: null,
    interval: null,
    repeatFrom: null,
    repeatUntil: null,
  };
};

/**
 * Extrait la fréquence d'une chaîne RRule
 * @param rruleString - Chaîne RRule à analyser
 * @returns La fréquence extraite ou la valeur par défaut (hebdomadaire)
 */
export const getRRuleFrequency = (rruleString: string): number => {
  try {
    const rruleSet = stringToRRuleSet(rruleString);
    const rules = rruleSet.rrules();
    if (rules.length > 0) {
      return rules[0].options.freq;
    }
    return Frequency.WEEKLY; // Valeur par défaut
  } catch (error) {
    return Frequency.WEEKLY; // Valeur par défaut
  }
};

/**
 * Extrait l'intervalle d'une chaîne RRule
 * @param rruleString - Chaîne RRule à analyser
 * @returns L'intervalle extrait ou la valeur par défaut (1)
 */
export const getRRuleInterval = (rruleString: string): number => {
  try {
    const rruleSet = stringToRRuleSet(rruleString);
    const rules = rruleSet.rrules();
    if (rules.length > 0) {
      return rules[0].options.interval || 1;
    }
    return 1; // Valeur par défaut
  } catch (error) {
    return 1; // Valeur par défaut
  }
};

/**
 * Crée un RRuleSet à partir des options et des dates d'exclusion
 * @param options - Options de règle de récurrence
 * @param exdates - Dates à exclure de la récurrence
 * @returns RRuleSet configuré
 */
const createRRuleSet = (options: RRuleOptions, exdates: Date[] = []): RRuleSet => {
  const rruleSet = new RRuleSet();

  // Ajoute la règle principale
  rruleSet.rrule(new RRule(options));

  // Ajoute les dates d'exclusion
  exdates.forEach((date) => {
    rruleSet.exdate(date);
  });

  return rruleSet;
};

/**
 * Récupère les dates d'exclusion d'une chaîne RRule
 * @param rruleString - Chaîne RRule à analyser
 * @returns Liste des dates d'exclusion
 */
export const getRRuleExdates = (rruleString: string): Date[] => {
  const rruleSet = stringToRRuleSet(rruleString);
  return rruleSet.exdates();
};

/**
 * Récupère la date de début d'une chaîne RRule
 * @param rruleString - Chaîne RRule à analyser
 * @returns La date de début extraite ou null
 */
export const getRRuleFrom = (rruleString: string): Date | null => {
  try {
    const rruleSet = stringToRRuleSet(rruleString);
    const rules = rruleSet.rrules();
    if (rules.length > 0) {
      return rules[0].options.dtstart || null;
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Récupère la date de fin d'une chaîne RRule
 * @param rruleString - Chaîne RRule à analyser
 * @returns La date de fin extraite ou null
 */
export const getRRuleUntil = (rruleString: string): Date | null => {
  try {
    const rruleSet = stringToRRuleSet(rruleString);
    const rules = rruleSet.rrules();
    if (rules.length > 0) {
      return rules[0].options.until || null;
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Ajoute une date d'exclusion à une chaîne RRule
 * @param rruleString - Chaîne RRule à modifier
 * @param date - Date à exclure
 * @returns Nouvelle chaîne RRule avec la date d'exclusion
 */
export const addExcludedDate = (rruleString: string, date: Date): string => {
  const rruleSet = stringToRRuleSet(rruleString);
  rruleSet.exdate(date);
  return rruleSetToString(rruleSet);
};

/**
 * Supprime une date d'exclusion d'une chaîne RRule
 * @param rruleString - Chaîne RRule à modifier
 * @param dateToRemove - Date à supprimer des exclusions
 * @returns Nouvelle chaîne RRule sans la date d'exclusion
 */
export const removeExcludedDate = (rruleString: string, dateToRemove: Date): string => {
  const rruleSet = stringToRRuleSet(rruleString);
  // RRuleSet ne fournit pas de méthode directe pour supprimer une exdate
  // On doit donc créer un nouveau RRuleSet avec toutes les règles et exclusions sauf celle à supprimer
  const newRRuleSet = new RRuleSet();

  // Copier toutes les règles
  rruleSet.rrules().forEach((rule) => {
    newRRuleSet.rrule(rule);
  });

  // Copier toutes les exclusions sauf celle à supprimer
  rruleSet.exdates().forEach((date) => {
    if (date.getTime() !== dateToRemove.getTime()) {
      newRRuleSet.exdate(date);
    }
  });

  return rruleSetToString(newRRuleSet);
};

/**
 * Génère une chaîne RRule à partir des valeurs du formulaire
 * @param values - Valeurs du formulaire
 * @param exdates - Dates à exclure de la récurrence
 * @returns Chaîne RRule générée ou null si la récurrence n'est pas activée
 */
export const generateRRuleString = (
  values: {
    isRecurring: boolean;
    frequency: number;
    interval: number;
    start: Date | string;
    repeatUntil: Date | string | null;
  },
  exdates: Date[] = []
): string | null => {
  if (!values.isRecurring) return null;

  const options: Partial<RRuleOptions> = {
    freq: values.frequency,
    dtstart: new Date(values.start),
    interval: values.interval || 1,
  };

  // Ajouter la date de fin si elle est définie
  if (values.repeatUntil) {
    options.until = new Date(values.repeatUntil);
  }

  // Créer un RRuleSet avec la règle principale et les dates d'exclusion
  const rruleSet = createRRuleSet(options as RRuleOptions, exdates);

  // Retourner la chaîne RRule
  return rruleSetToString(rruleSet);
};

/**
 * Obtient une description lisible de la récurrence
 * @param frequency - Fréquence de récurrence
 * @param interval - Intervalle de récurrence
 * @param until - Date de fin
 * @returns Description de la récurrence
 */
export const getRecurrenceDescription = ({
  frequency,
  interval,
  from,
  until,
  capitalize = false,
}: {
  frequency: number;
  interval: number;
  from?: Date | null;
  until?: Date | null;
  capitalize?: boolean;
}): string => {
  let frequencyText = '';

  if (frequency === Frequency.DAILY) {
    frequencyText = interval === 1 ? 'chaque jour' : `tous les ${interval} jours`;
  } else if (frequency === Frequency.WEEKLY) {
    frequencyText = interval === 1 ? 'chaque semaine' : `toutes les ${interval} semaines`;
  } else if (frequency === Frequency.MONTHLY) {
    frequencyText = interval === 1 ? 'chaque mois' : `tous les ${interval} mois`;
  }

  frequencyText = capitalize
    ? frequencyText.charAt(0).toUpperCase() + frequencyText.slice(1)
    : frequencyText;

  const rangeText = from
    ? until
      ? `\ndu ${dayjs(from).format('DD MMM')} au ${dayjs(until).format('DD MMM YYYY')}`
      : `\nà partir du ${dayjs(from).format('DD MMM YYYY')}`
    : until
    ? `\njusqu'au ${dayjs(until).format('DD MMM YYYY')}`
    : '';

  return `${frequencyText}${rangeText}`;
};

export const getExcludedDatesDescription = (rruleString: string): string => {
  try {
    const rruleSet = stringToRRuleSet(rruleString);
    console.log(rruleString);
    console.log(rruleSet.rrules());
    console.log(rruleSet.exdates());
    const excludedDates = rruleSet.exdates();
    return excludedDates?.length > 0
      ? `sauf le${excludedDates.length > 1 ? 's' : ''} : ${excludedDates
          .map((d: Date) => dayjs(d).format('DD/MM'))
          .join(', ')}`
      : '';
  } catch (e) {
    console.log(e);
  }
  return '';
};
